import React, { createContext, useContext, useState, useMemo } from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'

const ThemeContext = createContext()

// Цветовые схемы - выносим наружу, чтобы были доступны везде
export const colorSchemes = {
  blue: {
    name: 'Синяя',
    light: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
    dark: { main: '#90caf9', light: '#e3f2fd', dark: '#42a5f5' }
  },
  green: {
    name: 'Зеленая',
    light: { main: '#2e7d32', light: '#4caf50', dark: '#1b5e20' },
    dark: { main: '#81c784', light: '#e8f5e8', dark: '#4caf50' }
  },
  purple: {
    name: 'Фиолетовая',
    light: { main: '#7b1fa2', light: '#9c27b0', dark: '#6a1b9a' },
    dark: { main: '#ba68c8', light: '#f3e5f5', dark: '#9c27b0' }
  },
  orange: {
    name: 'Оранжевая',
    light: { main: '#f57c00', light: '#ff9800', dark: '#e65100' },
    dark: { main: '#ffb74d', light: '#fff3e0', dark: '#ff9800' }
  },
  teal: {
    name: 'Бирюзовая',
    light: { main: '#00796b', light: '#009688', dark: '#004d40' },
    dark: { main: '#4db6ac', light: '#e0f2f1', dark: '#009688' }
  }
}

const secondaryColors = {
  blue: '#dc004e',
  green: '#ff6f00', 
  purple: '#ff4081',
  orange: '#2979ff',
  teal: '#ff6d00'
}

export const useThemeContext = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeContext must be used within a CustomThemeProvider')
  }
  return context
}

export const CustomThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode')
    return saved ? JSON.parse(saved) : false
  })
  
  const [colorScheme, setColorScheme] = useState(() => {
    const saved = localStorage.getItem('colorScheme')
    return saved || 'blue'
  })

  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: colorSchemes[colorScheme][darkMode ? 'dark' : 'light'],
      secondary: {
        main: secondaryColors[colorScheme],
        light: darkMode ? '#fce4ec' : '#ff5983',
        dark: darkMode ? '#ad1457' : '#9a0036',
      },
      background: {
        default: darkMode ? '#121212' : '#f5f5f5',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: darkMode 
              ? '0 2px 8px rgba(0,0,0,0.3)' 
              : '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode 
              ? colorSchemes[colorScheme].dark.main
              : colorSchemes[colorScheme].light.main,
          },
        },
      },
    },
  }), [darkMode, colorScheme])

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode
    setDarkMode(newDarkMode)
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode))
  }

  const changeColorScheme = (scheme) => {
    setColorScheme(scheme)
    localStorage.setItem('colorScheme', scheme)
  }

  const value = {
    darkMode,
    toggleDarkMode,
    colorScheme,
    changeColorScheme,
    colorSchemes: Object.keys(colorSchemes),
    getColorSchemeName: (scheme) => colorSchemes[scheme]?.name || scheme,
    colorSchemesData: colorSchemes // Добавляем полные данные о схемах
  }

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  )
}